/**
 * Administration → Letterhead. Upload the organisation's official pad as an
 * HTML file; every generated PDF (quotations today, anything else later) is
 * printed onto it. Placeholders in the HTML: {{content}} (required — where the
 * document goes), {{org_name}}, {{title}}, {{date}}.
 */
import { useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useDispatch } from 'react-redux';
import { PageHeader, Card, Spinner, ErrorState } from '../../components/common/index.jsx';
import { ButtonSpinner } from '../../components/common/Loading.jsx';
import { useAuth } from '../../hooks/useAuth.js';
import { pushToast } from '../ui/uiSlice.js';
import * as api from '../../api/letterhead.api.js';
import { dateTime } from '../../utils/formatters.js';

const SAMPLE = '<h2 style="margin:0 0 6px">QUOTATION QT-00000</h2><p>Sample document body — your quotation or letter appears here.</p>'
  + '<table style="width:100%;border-collapse:collapse"><tr><th align="left">Item</th><th align="right">Total</th></tr><tr><td>Product</td><td align="right">USD 1,000.00</td></tr></table>';

const preview = (html) => html
  .replace(/\{\{\s*content\s*\}\}/g, SAMPLE)
  .replace(/\{\{\s*org_name\s*\}\}/g, 'Your organisation')
  .replace(/\{\{\s*title\s*\}\}/g, 'Sample')
  .replace(/\{\{\s*date\s*\}\}/g, new Date().toISOString().slice(0, 10));

export default function LetterheadPage() {
  const qc = useQueryClient();
  const dispatch = useDispatch();
  const { can } = useAuth();
  const canEdit = can('admin.master', 'edit');
  const fileRef = useRef(null);
  const [draft, setDraft] = useState(null); // { html, file_name } picked but not yet saved
  const [error, setError] = useState('');

  const q = useQuery({ queryKey: ['letterhead'], queryFn: api.getLetterhead });
  const toast = (tone, message, detail) => dispatch(pushToast({ tone, message, detail }));

  const save = useMutation({
    mutationFn: api.saveLetterhead,
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['letterhead'] }); setDraft(null); toast('success', 'Letterhead saved'); },
    onError: (e) => toast('danger', 'Could not save the letterhead', e.message),
  });
  const remove = useMutation({
    mutationFn: api.removeLetterhead,
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['letterhead'] }); toast('success', 'Letterhead removed'); },
    onError: (e) => toast('danger', 'Could not remove it', e.message),
  });

  const pick = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    setError('');
    if (file.size > 2 * 1024 * 1024) { setError('The file must be under 2 MB.'); return; }
    const html = await file.text();
    if (!/\{\{\s*content\s*\}\}/.test(html)) {
      setError('Heads up: no {{content}} placeholder found, so documents will be added at the end of the page.');
    }
    setDraft({ html, file_name: file.name });
  };

  if (q.isLoading) return <Spinner />;
  if (q.isError) return <ErrorState title="Could not load the letterhead" message={q.error.message} />;

  const current = q.data?.html ? { html: q.data.html, file_name: q.data.file_name } : null;
  const shown = draft ?? current;

  return (
    <>
      <PageHeader title="Letterhead" subtitle="Your official pad — every PDF sent to clients is printed on it" />
      <div className="row g-3">
        <div className="col-lg-4">
          <Card title="Upload">
            <p className="t-sm text-muted">
              Upload one HTML file with your header, footer and branding. Put <code>{'{{content}}'}</code> where the
              document should go. Also available: <code>{'{{org_name}}'}</code>, <code>{'{{title}}'}</code>, <code>{'{{date}}'}</code>.
              Use inline CSS and embed images as data URIs (or public https links) — scripts are ignored.
            </p>
            {current && !draft && (
              <div className="t-sm mb-3">
                <b>{current.file_name ?? 'letterhead.html'}</b>
                {q.data.updated_at && <div className="text-muted">Updated {dateTime(q.data.updated_at)}</div>}
              </div>
            )}
            {error && <div className="alert alert-warning py-2 t-sm">{error}</div>}
            <input ref={fileRef} type="file" accept=".html,.htm,text/html" hidden onChange={pick} />
            {canEdit ? (
              <div className="d-flex flex-wrap gap-2">
                <button type="button" className="btn btn-edit btn-sm-x" onClick={() => fileRef.current?.click()}>
                  <i className="bi bi-upload me-1" aria-hidden="true" />{current ? 'Choose a new file' : 'Choose HTML file'}
                </button>
                {draft && (
                  <>
                    <button type="button" className="btn btn-save btn-sm-x" disabled={save.isPending} onClick={() => save.mutate(draft)}>
                      {save.isPending ? <><ButtonSpinner />Saving…</> : `Save ${draft.file_name}`}
                    </button>
                    <button type="button" className="btn btn-cancel btn-sm-x" onClick={() => { setDraft(null); setError(''); }}>Cancel</button>
                  </>
                )}
                {current && !draft && (
                  <button type="button" className="btn btn-delete btn-sm-x" disabled={remove.isPending} onClick={() => remove.mutate()}>Remove</button>
                )}
              </div>
            ) : <div className="t-sm text-muted">You can view the letterhead but not change it.</div>}
          </Card>
        </div>
        <div className="col-lg-8">
          <Card title="Preview" subtitle={draft ? 'Not saved yet' : undefined}>
            {shown ? (
              <iframe
                title="Letterhead preview" sandbox="" srcDoc={preview(shown.html)}
                style={{ width: '100%', height: 560, border: '1px solid var(--line-2)', borderRadius: 'var(--r)', background: '#fff' }}
              />
            ) : (
              <div className="t-sm text-muted">No letterhead yet — PDFs are generated on a plain white page.</div>
            )}
          </Card>
        </div>
      </div>
    </>
  );
}
